import Component from '@glimmer/component';

/**
 * Live showcase of the @databox/theme token stack running through Tailwind in
 * the Ember light DOM (see app/styles/tailwind.css).
 *
 * Every Tailwind class must appear in this source as a complete, literal
 * string — the extractor scans raw text and cannot resolve interpolation, so
 * `tw:bg-{{name}}` would silently produce nothing. That is why the scale arrays
 * below carry the full class name rather than composing it in the template.
 */

interface Swatch {
	label: string;
	surface: string;
	foreground: string;
}

interface Scale {
	label: string;
	value: string;
	demo: string;
}

export default class StorybookDesignTokensComponent extends Component {
	surfaces: Swatch[] = [
		{ label: 'background', surface: 'tw:bg-background', foreground: 'tw:text-foreground' },
		{ label: 'card', surface: 'tw:bg-card', foreground: 'tw:text-card-foreground' },
		{ label: 'popover', surface: 'tw:bg-popover', foreground: 'tw:text-popover-foreground' },
		{ label: 'muted', surface: 'tw:bg-muted', foreground: 'tw:text-muted-foreground' },
		{
			label: 'secondary',
			surface: 'tw:bg-secondary',
			foreground: 'tw:text-secondary-foreground',
		},
		{ label: 'accent', surface: 'tw:bg-accent', foreground: 'tw:text-accent-foreground' },
		{ label: 'sidebar', surface: 'tw:bg-sidebar', foreground: 'tw:text-sidebar-foreground' },
		{
			label: 'sidebar-core',
			surface: 'tw:bg-sidebar-core',
			foreground: 'tw:text-sidebar-foreground',
		},
	];

	statuses: Swatch[] = [
		{ label: 'primary', surface: 'tw:bg-primary', foreground: 'tw:text-primary-foreground' },
		{
			label: 'primary-muted',
			surface: 'tw:bg-primary-muted',
			foreground: 'tw:text-foreground',
		},
		{ label: 'success', surface: 'tw:bg-success', foreground: 'tw:text-success-foreground' },
		{
			label: 'success-muted',
			surface: 'tw:bg-success-muted',
			foreground: 'tw:text-foreground',
		},
		{ label: 'danger', surface: 'tw:bg-danger', foreground: 'tw:text-danger-foreground' },
		{ label: 'danger-muted', surface: 'tw:bg-danger-muted', foreground: 'tw:text-foreground' },
	];

	accents: Swatch[] = [
		{ label: 'accent-color-1', surface: 'tw:bg-accent-color-1', foreground: 'tw:text-white-0' },
		{ label: 'accent-color-2', surface: 'tw:bg-accent-color-2', foreground: 'tw:text-white-0' },
		{ label: 'accent-color-3', surface: 'tw:bg-accent-color-3', foreground: 'tw:text-white-0' },
		{
			label: 'accent-color-4',
			surface: 'tw:bg-accent-color-4',
			foreground: 'tw:text-neutral-925',
		},
		{ label: 'accent-color-5', surface: 'tw:bg-accent-color-5', foreground: 'tw:text-white-0' },
		{
			label: 'accent-color-6',
			surface: 'tw:bg-accent-color-6',
			foreground: 'tw:text-neutral-925',
		},
	];

	spacings: Scale[] = [
		{ label: '0-5', value: '2px', demo: 'tw:h-0-5' },
		{ label: '1', value: '4px', demo: 'tw:h-1' },
		{ label: '1-5', value: '6px', demo: 'tw:h-1-5' },
		{ label: '2', value: '8px', demo: 'tw:h-2' },
		{ label: '3', value: '12px', demo: 'tw:h-3' },
		{ label: '4', value: '16px', demo: 'tw:h-4' },
		{ label: '6', value: '24px', demo: 'tw:h-6' },
		{ label: '8', value: '32px', demo: 'tw:h-8' },
		{ label: '12', value: '48px', demo: 'tw:h-12' },
	];

	radii: Scale[] = [
		{ label: 'xs', value: '2px', demo: 'tw:rounded-xs' },
		{ label: 'ms', value: '4px', demo: 'tw:rounded-ms' },
		{ label: 'sm', value: '6px', demo: 'tw:rounded-sm' },
		{ label: 'md', value: '8px', demo: 'tw:rounded-md' },
		{ label: 'lg', value: '10px', demo: 'tw:rounded-lg' },
		{ label: 'xl', value: '12px', demo: 'tw:rounded-xl' },
		{ label: '2xl', value: '16px', demo: 'tw:rounded-2xl' },
		{ label: '3xl', value: '20px', demo: 'tw:rounded-3xl' },
		{ label: '4xl', value: '24px', demo: 'tw:rounded-4xl' },
		{ label: '5xl', value: '32px', demo: 'tw:rounded-5xl' },
		{ label: 'full', value: '9999px', demo: 'tw:rounded-full' },
	];

	shadows: Scale[] = [
		{ label: 'shadow-surface-sm', value: 'resting panel', demo: 'tw:shadow-surface-sm' },
		{ label: 'shadow-surface-md', value: 'media thumbnail', demo: 'tw:shadow-surface-md' },
		{ label: 'shadow-surface-lg', value: 'active segment', demo: 'tw:shadow-surface-lg' },
		{ label: 'shadow-surface-composer', value: 'composer', demo: 'tw:shadow-surface-composer' },
		{
			label: 'shadow-floating-tooltip',
			value: 'floating bar, banner',
			demo: 'tw:shadow-floating-tooltip',
		},
		{ label: 'shadow-floating-menu', value: 'menu, popover', demo: 'tw:shadow-floating-menu' },
		{
			label: 'shadow-floating-modal',
			value: 'dialog, side panel',
			demo: 'tw:shadow-floating-modal',
		},
	];

	typography: Scale[] = [
		{ label: 'text-xs', value: '12 / 16', demo: 'tw:text-xs' },
		{ label: 'text-sm', value: '14 / 20', demo: 'tw:text-sm' },
		{ label: 'text-base', value: '16 / 24', demo: 'tw:text-base' },
		{ label: 'text-lg', value: '18 / 28', demo: 'tw:text-lg' },
		{ label: 'text-xl', value: '20 / 28', demo: 'tw:text-xl' },
		{ label: 'text-2xl', value: '24 / 32', demo: 'tw:text-2xl' },
		{ label: 'text-3xl', value: '30 / 36', demo: 'tw:text-3xl' },
		{ label: 'text-4xl', value: '36 / 40', demo: 'tw:text-4xl' },
		{ label: 'text-5xl', value: '48 / 48', demo: 'tw:text-5xl' },
	];

	<template>
		<h1>Design Tokens <em>(@databox/theme via Tailwind)</em></h1>

		<h2>Surfaces &amp; foregrounds</h2>

		<div class="tw:grid tw:grid-cols-4 tw:gap-2">
			{{#each this.surfaces as |swatch|}}
				<div
					class="tw:flex tw:flex-col tw:gap-0-5 tw:rounded-md tw:border tw:border-border tw:p-4
						{{swatch.surface}}
						{{swatch.foreground}}"
				>
					<span class="tw:text-sm">{{swatch.label}}</span>
					<span class="tw:text-xs tw:opacity-70">Aa</span>
				</div>
			{{/each}}
		</div>

		<h2>Status</h2>

		<div class="tw:grid tw:grid-cols-3 tw:gap-2">
			{{#each this.statuses as |swatch|}}
				<div
					class="tw:rounded-md tw:border tw:border-border tw:p-4 tw:text-sm
						{{swatch.surface}}
						{{swatch.foreground}}"
				>
					{{swatch.label}}
				</div>
			{{/each}}
		</div>

		<h2>Accent colors</h2>

		<div class="tw:grid tw:grid-cols-6 tw:gap-2">
			{{#each this.accents as |swatch|}}
				<div
					class="tw:rounded-md tw:p-4 tw:text-xs {{swatch.surface}} {{swatch.foreground}}"
				>
					{{swatch.label}}
				</div>
			{{/each}}
		</div>

		<h2>Spacing scale</h2>

		<div class="tw:flex tw:flex-col tw:gap-1">
			{{#each this.spacings as |step|}}
				<div class="tw:flex tw:items-center tw:gap-3">
					<span class="tw:w-16 tw:text-xs tw:text-muted-foreground">{{step.label}}</span>
					<span class="tw:w-16 tw:text-xs tw:text-muted-foreground">{{step.value}}</span>
					<div class="tw:w-40 tw:rounded-xs tw:bg-primary {{step.demo}}"></div>
				</div>
			{{/each}}
		</div>

		<h2>Radius scale</h2>

		<div class="tw:grid tw:grid-cols-11 tw:gap-2">
			{{#each this.radii as |step|}}
				<div class="tw:flex tw:flex-col tw:items-center tw:gap-1">
					<div
						class="tw:h-12 tw:w-full tw:bg-muted tw:border tw:border-border
							{{step.demo}}"
					></div>
					<span class="tw:text-xs tw:text-muted-foreground">{{step.label}}</span>
					<span class="tw:text-xs tw:text-muted-foreground">{{step.value}}</span>
				</div>
			{{/each}}
		</div>

		<h2>Shadows</h2>

		<div class="tw:grid tw:grid-cols-3 tw:gap-6 tw:p-4">
			{{#each this.shadows as |step|}}
				<div
					class="tw:rounded-md tw:bg-card tw:p-6 tw:text-sm tw:text-card-foreground
						{{step.demo}}"
				>
					{{step.label}}
					<span class="tw:text-muted-foreground">({{step.value}})</span>
				</div>
			{{/each}}
		</div>

		<h2>Typography</h2>

		<p class="tw:text-sm tw:text-muted-foreground">
			The theme also ships text-6xl … text-9xl (60/60, 72/72, 96/96, 128/128).
		</p>

		<div class="tw:flex tw:flex-col tw:gap-2">
			{{#each this.typography as |step|}}
				<div class="tw:flex tw:items-baseline tw:gap-3">
					<span class="tw:w-40 tw:text-xs tw:text-muted-foreground">{{step.label}}</span>
					<span class="tw:w-16 tw:text-xs tw:text-muted-foreground">{{step.value}}</span>
					<span class="tw:text-foreground {{step.demo}}">The quick brown fox</span>
				</div>
			{{/each}}
		</div>

		<h2>Dark mode</h2>

		<p class="tw:text-sm tw:text-muted-foreground">
			mode.css scopes the dark token values to a
			<code>.tw:dark</code>
			class rather than a media query, so a container can opt in locally. The right-hand panel
			below is the same markup wrapped in
			<code>tw:dark</code>
			— no app-wide theme switch involved. The body-level class is kept in sync with the
			legacy
			<code>theme-light</code>
			/
			<code>theme-dark</code>
			class by app/models-custom/theme.ts.
		</p>

		<div class="tw:grid tw:grid-cols-2 tw:gap-4">
			<div class="tw:rounded-md tw:border tw:border-border tw:bg-background tw:p-6">
				<div class="tw:text-xs tw:text-muted-foreground">light (inherited)</div>
				<div class="tw:mt-2 tw:flex tw:gap-2">
					<div
						class="tw:rounded-sm tw:border tw:border-border tw:bg-card tw:p-4 tw:text-sm tw:text-card-foreground tw:shadow-surface-sm"
					>
						card
					</div>
					<div
						class="tw:rounded-sm tw:bg-primary tw:p-4 tw:text-sm tw:text-primary-foreground"
					>
						primary
					</div>
					<div
						class="tw:rounded-sm tw:bg-muted tw:p-4 tw:text-sm tw:text-muted-foreground"
					>
						muted
					</div>
				</div>
			</div>

			<div class="tw:dark tw:rounded-md tw:border tw:border-border tw:bg-background tw:p-6">
				<div class="tw:text-xs tw:text-muted-foreground">dark (scoped)</div>
				<div class="tw:mt-2 tw:flex tw:gap-2">
					<div
						class="tw:rounded-sm tw:border tw:border-border tw:bg-card tw:p-4 tw:text-sm tw:text-card-foreground tw:shadow-surface-sm"
					>
						card
					</div>
					<div
						class="tw:rounded-sm tw:bg-primary tw:p-4 tw:text-sm tw:text-primary-foreground"
					>
						primary
					</div>
					<div
						class="tw:rounded-sm tw:bg-muted tw:p-4 tw:text-sm tw:text-muted-foreground"
					>
						muted
					</div>
				</div>
			</div>
		</div>

		<h2>Variants</h2>

		<div class="tw:flex tw:gap-2">
			<div
				class="tw:rounded-md tw:border tw:border-border tw:bg-card tw:p-4 tw:text-sm tw:text-card-foreground tw:transition-colors tw:hover:bg-accent"
			>
				hover:bg-accent
			</div>
			<div class="tw:rounded-md tw:bg-muted tw:p-4 tw:text-sm tw:dark:bg-primary">
				dark:bg-primary
			</div>
			<div class="tw:rounded-md tw:bg-muted tw:p-4 tw:text-sm tw:md:bg-success-muted">
				md:bg-success-muted
			</div>
		</div>

		<h2>Coexistence with legacy LESS</h2>

		<p class="tw:text-sm tw:text-muted-foreground">
			The outer box is a legacy
			<code>.item</code>
			from storybook.less, styled by
			<code>var(--border-color-1)</code>. The inner box is Tailwind. The two token systems
			share zero variable names, so neither overwrites the other.
		</p>

		<div class="grid">
			<div class="item">
				<div class="item__title">legacy .item / .item__title</div>
				<div class="item__content">
					<div
						class="tw:rounded-md tw:bg-primary-muted tw:p-4 tw:text-sm tw:text-foreground"
					>
						nested Tailwind box
					</div>
				</div>
			</div>
		</div>
	</template>
}
